/**
 * @fileoverview Componente responsável por executar ações em massa sobre tickets selecionados.
 */

import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  makeStyles,
  Chip,
  Box,
  Typography
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useQueues } from "../../context/Queues/QueuesContext";

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      background: theme.palette.type === 'dark'
        ? 'linear-gradient(135deg, rgba(18, 18, 19, 0.95) 0%, rgba(22, 22, 24, 0.95) 100%)'
        : '#ffffff',
      border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.2)' : 'none',
      borderRadius: '24px',
      boxShadow: theme.palette.type === 'dark'
        ? '0 8px 32px rgba(0, 0, 0, 0.4)'
        : '0 8px 32px rgba(0, 0, 0, 0.1)',
    },
  },
  dialogTitle: {
    background: theme.palette.type === 'dark'
      ? 'linear-gradient(135deg, rgba(91, 124, 255, 0.1) 0%, rgba(157, 124, 255, 0.1) 100%)'
      : 'linear-gradient(135deg, rgba(61, 92, 253, 0.05) 0%, rgba(138, 35, 135, 0.05) 100%)',
    borderBottom: theme.palette.type === 'dark'
      ? '1px solid rgba(91, 124, 255, 0.2)'
      : '1px solid rgba(0, 0, 0, 0.08)',
    color: theme.palette.type === 'dark' ? '#ffffff' : '#1a1a1a',
    fontWeight: 600,
  },
  dialogActions: {
    background: theme.palette.type === 'dark'
      ? 'linear-gradient(135deg, rgba(15, 15, 17, 0.5) 0%, rgba(20, 20, 22, 0.5) 100%)'
      : '#fafafa',
    borderTop: theme.palette.type === 'dark'
      ? '1px solid rgba(91, 124, 255, 0.2)'
      : '1px solid rgba(0, 0, 0, 0.08)',
    padding: theme.spacing(2),
  },
  formControl: {
    marginBottom: theme.spacing(2),
    minWidth: '100%',
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  required: {
    color: theme.palette.error.main,
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      background: theme.palette.type === 'dark'
        ? 'rgba(22, 22, 24, 0.6)'
        : '#ffffff',
      borderRadius: '14px',
      '& fieldset': {
        borderColor: theme.palette.type === 'dark'
          ? 'rgba(91, 124, 255, 0.2)'
          : 'rgba(0, 0, 0, 0.23)',
      },
      '&:hover fieldset': {
        borderColor: theme.palette.type === 'dark'
          ? 'rgba(91, 124, 255, 0.4)'
          : 'rgba(0, 0, 0, 0.4)',
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.type === 'dark'
          ? 'rgba(91, 124, 255, 0.6)'
          : theme.palette.primary.main,
      },
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.7)' : undefined,
    },
    '& .MuiInputBase-input': {
      color: theme.palette.type === 'dark' ? '#ffffff' : undefined,
    },
  },
  select: {
    '& .MuiOutlinedInput-root': {
      background: theme.palette.type === 'dark'
        ? 'rgba(22, 22, 24, 0.6)'
        : '#ffffff',
      borderRadius: '14px',
      '& fieldset': {
        borderColor: theme.palette.type === 'dark'
          ? 'rgba(91, 124, 255, 0.2)'
          : 'rgba(0, 0, 0, 0.23)',
      },
      '&:hover fieldset': {
        borderColor: theme.palette.type === 'dark'
          ? 'rgba(91, 124, 255, 0.4)'
          : 'rgba(0, 0, 0, 0.4)',
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.type === 'dark'
          ? 'rgba(91, 124, 255, 0.6)'
          : theme.palette.primary.main,
      },
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.7)' : undefined,
    },
    '& .MuiSelect-root': {
      color: theme.palette.type === 'dark' ? '#ffffff' : undefined,
    },
  },
}));

const BulkActionsModal = ({ open, onClose, actionType }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  // Campos obrigatórios
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedConnection, setSelectedConnection] = useState("");

  // Campos opcionais
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);

  // Listas
  const [connections, setConnections] = useState([]);
  const [users, setUsers] = useState([]);
  const { queues } = useQueues();
  const [tags, setTags] = useState([]);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchData();
    } else {
      // Limpar campos ao fechar
      setStartDate("");
      setEndDate("");
      setSelectedConnection("");
      setSelectedUser(null);
      setSelectedQueue(null);
      setSelectedTags([]);
    }
  }, [open]);

  const fetchData = async () => {
    try {
      const { data: whatsapps } = await api.get("/whatsapp");
      setConnections(whatsapps);

      if (user.profile === "admin") {
        const { data: usersData } = await api.get("/users");
        setUsers(usersData.users);
      }

      const { data: tagsData } = await api.get("/tags");
      setTags(tagsData);
    } catch (err) {
      toastError(err);
    }
  };

  const handleSubmit = async () => {
    if (!startDate) {
      toast.error("Selecione a data inicial");
      return;
    }

    if (!endDate) {
      toast.error("Selecione a data final");
      return;
    }

    if (!selectedConnection) {
      toast.error("Selecione uma conexão");
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      toast.error("Data inicial não pode ser maior que data final");
      return;
    }

    setLoading(true);

    try {
      const params = {
        startDate,
        endDate,
        whatsappId: selectedConnection,
      };

      if (selectedUser) params.userId = selectedUser.id;
      if (selectedQueue) params.queueId = selectedQueue.id;
      if (selectedTags.length > 0) params.tagIds = selectedTags.map(t => t.id);

      const endpoint = actionType === "accept" ? "/tickets/bulk-accept" : "/tickets/bulk-resolve";
      const { data } = await api.post(endpoint, params);

      toast.success(`${data.count} ticket(s) ${actionType === "accept" ? "aceito(s)" : "resolvido(s)"} com sucesso!`);
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      className={classes.dialog}
    >
      <DialogTitle className={classes.dialogTitle}>
        {actionType === "accept" ? "Aceitar tickets em massa" : "Resolver tickets em massa"}
      </DialogTitle>

      <DialogContent>
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            Campos obrigatórios <span className={classes.required}>*</span>
          </Typography>

          <FormControl className={classes.formControl}>
            <TextField
              label="Data Inicial"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              required
              fullWidth
              className={classes.textField}
            />
          </FormControl>

          <FormControl className={classes.formControl}>
            <TextField
              label="Data Final"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              required
              fullWidth
              className={classes.textField}
            />
          </FormControl>

          <FormControl className={`${classes.formControl} ${classes.select}`} variant="outlined">
            <InputLabel>Conexão *</InputLabel>
            <Select
              value={selectedConnection}
              onChange={(e) => setSelectedConnection(e.target.value)}
              label="Conexão *"
              required
            >
              {connections.map((conn) => (
                <MenuItem key={conn.id} value={conn.id}>
                  {conn.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            Filtros opcionais
          </Typography>

          {user.profile === "admin" && (
            <FormControl className={classes.formControl}>
              <Autocomplete
                value={selectedUser}
                onChange={(event, newValue) => setSelectedUser(newValue)}
                options={users}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField {...params} label="Usuário" className={classes.textField} />
                )}
              />
            </FormControl>
          )}

          <FormControl className={classes.formControl}>
            <Autocomplete
              value={selectedQueue}
              onChange={(event, newValue) => setSelectedQueue(newValue)}
              options={queues}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => (
                <TextField {...params} label="Fila" className={classes.textField} />
              )}
            />
          </FormControl>

          <FormControl className={classes.formControl}>
            <Autocomplete
              multiple
              value={selectedTags}
              onChange={(event, newValue) => setSelectedTags(newValue)}
              options={tags}
              getOptionLabel={(option) => option.name}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={option.id}
                    label={option.name}
                    {...getTagProps({ index })}
                    style={{
                      backgroundColor: option.color,
                      color: '#fff'
                    }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Tags" className={classes.textField} />
              )}
            />
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button onClick={onClose} color="default">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading}
        >
          {loading ? "Processando..." : actionType === "accept" ? "ACEITAR" : "RESOLVER"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkActionsModal;
